import React, { useCallback, useEffect, useState } from 'react';
import styles from '../../pages/CheckoutPage.module.css';
import UpiCollectSection from './UpiCollectSection';
import razorpayUtils from '../../utils/razorpayUtils';
import gpayLogo from '../../assets/upi/gpay.svg';
import phonepeLogo from '../../assets/upi/phonepe.svg';
import paytmLogo from '../../assets/upi/paytm.svg';
import bhimLogo from '../../assets/upi/bhim.svg';
import qrCodeIcon from '../../assets/upi/qrcode.png';
import upiLogo from '../../assets/upi/upi-logo.svg';

const { loadRazorpaySDK } = razorpayUtils;

// Official app logos — CDN primary, local SVG fallback
// All known app definitions — used for logo/label lookup when Razorpay returns apps
const ALL_UPI_APPS = [
  { id: 'gpay',     label: 'Google Pay', icon: '',                                                                                                                          fallback: gpayLogo    },
  { id: 'phonepe',  label: 'PhonePe',    icon: '',                                                                                                                          fallback: phonepeLogo },
  { id: 'paytm',    label: 'PayTM',      icon: '',                                                                                                                          fallback: paytmLogo   },
  { id: 'bhim',     label: 'BHIM',       icon: '',                                                                                                                          fallback: bhimLogo    },
  { id: 'cred',     label: 'CRED UPI',   icon: 'https://cdn.brandfetch.io/id27bJP5LK/w/400/h/400/theme/dark/icon.jpeg?c=1bxid64Mup7aczewSAYMX&t=1668516348931',            fallback: upiLogo     },
  { id: 'navi',     label: 'Navi',       icon: 'https://cdn.brandfetch.io/idiYJcltMT/w/400/h/400/theme/dark/icon.png?c=1bxid64Mup7aczewSAYMX&t=1768927574147',             fallback: upiLogo     },
  { id: 'mobikwik', label: 'MobiKwik',   icon: 'https://cdn.brandfetch.io/idbB1uCn0I/w/400/h/400/theme/dark/icon.png?c=1bxid64Mup7aczewSAYMX&t=1751351467331',            fallback: upiLogo     },
  { id: 'slice',    label: 'POP UPI',    icon: '',                                                                                                                          fallback: upiLogo     },
];


// Default 5 preferred apps shown when Razorpay hasn't responded yet or on non-mobile
const DEFAULT_UPI_APPS = ['gpay', 'phonepe', 'cred', 'paytm', 'bhim']
  .map((id) => ALL_UPI_APPS.find((a) => a.id === id));

// Cashfree tiles (added 2026-09-13): these now genuinely target the named
// app (see cashfreeUtils.js's CASHFREE_APP_PROVIDERS comment for why a
// single generic button was replaced) — reuses the same icon/label/fallback
// entries ALL_UPI_APPS already has for Razorpay. Limited to gpay/phonepe/
// paytm, Cashfree's own provider enum's "true" UPI-style apps (cred/bhim
// aren't in that enum) — a generic "Other UPI App" tile is added separately
// below for anything else, which can't target a specific app (no known
// provider) so it still uses the old generic upi channel under the hood.
//
// Bug found live on STG (2026-09-17): this list was never checked against
// what's actually enabled for the merchant account — Cashfree's own
// eligibility API (entity_value:'wallet', which turns out to cover this
// SAME app.provider space — confirmed live, real responses never included
// 'gpay' or a 'paytm' nick at all) showed neither GPay nor Paytm are
// actually enabled here. Tapping Paytm failed with a real Cashfree error
// ("mode not enabled for merchant"); tapping GPay silently fell through to
// a generic UPI-collect-request flow instead of a real app-intent redirect
// (Cashfree's fallback behavior for an unsupported provider) — both looked
// like broken UPI intent but were really just offering un-enabled providers.
// Only PhonePe was present in both live samples and behaved correctly (a
// genuine bridge-page redirect). Filtered below via the same liveWallets
// prop WalletSection.jsx already uses, so this list tracks reality instead
// of a hardcoded guess.
const CASHFREE_UPI_APPS = ['gpay', 'phonepe', 'paytm', 'bhim']
  .map((id) => ALL_UPI_APPS.find((a) => a.id === id))
  .filter(Boolean);

// Razorpay shortcode → UPI_APPS id
const SHORTCODE_MAP = {
  google_pay:  'gpay',
  phonepe:     'phonepe',
  paytm:       'paytm',
  bhim:        'bhim',
  cred:        'cred',
  navi:        'navi',
  mobikwik:    'mobikwik',
  slice:       'slice',
  amazon_pay:  'amazon_pay',
  freecharge:  'freecharge',
  samsung_pay: 'samsung_pay',
  sbi:         'sbi',
  axis:        'axis',
  hdfc:        'hdfc',
  icici:       'icici',
  kotak:       'kotak',
  jupiter:     'jupiter',
  groww:       'groww',
};

// Android package name → UPI_APPS id (secondary match when shortcode unknown)
const PACKAGE_MAP = {
  'com.google.android.apps.nbu.paisa.user': 'gpay',
  'com.phonepe.app':                        'phonepe',
  'net.one97.paytm':                        'paytm',
  'in.org.npci.upiapp':                     'bhim',
  'com.dreamplug.androidapp':               'cred',
  'com.naviapp':                            'navi',
  'com.mobikwik_new':                       'mobikwik',
  'indwin.c3.shareapp':                     'slice',
  'in.amazon.mShop.android.shopping':       'amazon_pay',
  'com.freecharge.android':                 'freecharge',
  'com.samsung.android.spay':               'samsung_pay',
  'com.sbi.upi':                            'sbi',
  'com.upi.axispay':                        'axis',
  'com.axis.mobile':                        'axis',
  'com.snapwork.hdfc':                      'hdfc',
  'com.hdfcbank.payzapp':                   'hdfc',
  'com.csam.icici.bank.imobile':            'icici',
  'com.icicibank.pockets':                  'icici',
  'com.msf.kbank.mobile':                   'kotak',
  'money.jupiter':                          'jupiter',
  'com.nextbillion.groww':                  'groww',
  'com.lcode.ucoupi':                       'bhim',
  'com.euronet.iobupi':                     'bhim',
  'com.lcode.csbupi':                       'bhim',
  'com.bankofbaroda.upi':                   'bob',
  'com.mgs.pnbupi':                         'pnb',
  'com.Version1':                           'pnb',
  'com.dbs.in.digitalbank':                 'digibank',
  'com.idfcfirstbank.optimus':              'idfc',
  'com.fss.idfcpsp':                        'idfc',
  'com.YesBank':                            'yesbank',
  'in.irisbyyes.app':                       'yesbank',
  'com.indusind.indie':                     'indusind',
  'com.mgs.induspsp':                       'indusind',
  'com.fss.indus':                          'indusind',
  'com.rblbank.mobank':                     'rbl',
  'com.fampay.in':                          'fampay',
  'in.bajajfinservmarkets.app':             'bajaj',
  'com.tatadigital.tcp':                    'tata_neu',
  'indwin.c3.shareapp':                     'slice',
  'com.popclub.android':                    'slice',
  'com.iexceed.appzillon.ippbMB':           'dak_pay',
  'com.fss.ippbpsp':                        'dak_pay',
  'com.canarabank.mobility':                'canara',
  'com.IndianBank.IndOASIS':                'indian_bank',
  'com.infrasoft.uboi':                     'vyom',
  'money.super.payments':                   'super_money',
  'com.ausmallfinancebank.amb':             'au_bank',
  'com.infrasofttech.CentralBank':          'cent_mobile',
  'ai.wizely.android':                      'wizely',
};

function UpiIntentSection({
  gateway = 'razorpay',
  isAndroid,
  isIOS,
  isInApp,
  inAppName,
  processing,
  openedApp,
  razorpayKeyId,
  amount,
  onPay,
  onQrRequest,
  onChangeApp,
  liveWallets = [],
}) {
  const isMobile = isAndroid || isIOS;
  const isCashfree = gateway === 'cashfree';
  const [showCollect, setShowCollect] = useState(false);

  // Only show a named-app tile when the live eligibility list confirms this
  // merchant account actually has that provider enabled — see
  // CASHFREE_UPI_APPS's comment above. Before the live list has loaded
  // (liveWallets empty on first render), show none of the named tiles rather
  // than the old unfiltered 3 — a brief empty state is safer than offering a
  // tile that's about to fail.
  // Updated 2026-10-16: tiles now use real UPI intent (upi + channel:'link',
  // per-app link from the response payload), which doesn't depend on wallet
  // providers being enabled — so the wallet-eligibility filter is gone and
  // all named tiles always show.
  const cashfreeAppsToShow = CASHFREE_UPI_APPS;

  // Filtered list of apps actually installed on the device.
  const [availableApps, setAvailableApps] = useState(null);

  useEffect(() => {
    // Cashfree has no equivalent of getSupportedUpiIntentApps() (confirmed
    // gap — see plan) — never call the Razorpay SDK when Cashfree is active.
    if (isCashfree) return;
    if (!isMobile || !razorpayKeyId) {
      setAvailableApps(DEFAULT_UPI_APPS);
      return;
    }
    // SDK must be loaded before calling getSupportedUpiIntentApps —
    // window.Razorpay is undefined until razorpay.js script is injected.
    loadRazorpaySDK('custom')
      .then(() => {
        const rzp = new window.Razorpay({ key: razorpayKeyId });
        return rzp.getSupportedUpiIntentApps();
      })
      .then((apps) => {
          const seen = new Set();
          const result = (apps || []).map((a) => {
            // 1. Try shortcode map
            const knownId = SHORTCODE_MAP[a.shortcode] || PACKAGE_MAP[a.package_name];
            if (knownId) {
              const known = ALL_UPI_APPS.find((u) => u.id === knownId);
              if (known) return known;
            }
            // 2. Unknown app — show dynamically with generic UPI icon
            return {
              id: a.shortcode || a.package_name || a.name,
              label: a.name || a.shortcode,
              icon: '',
              fallback: upiLogo,
            };
          }).filter((a) => {
            if (!a || !a.id) return false;
            if (seen.has(a.id)) return false;
            seen.add(a.id);
            return true;
          });
          setAvailableApps(result.length > 0 ? result : DEFAULT_UPI_APPS);
        })
        .catch(() => setAvailableApps(DEFAULT_UPI_APPS));
  }, [isMobile, razorpayKeyId, isCashfree]);

  const appsToShow = availableApps ?? DEFAULT_UPI_APPS;
  const amtDisplay = `₹${Number(amount).toLocaleString('en-IN')}`;

  // ── Awaiting payment (after app opened) ──
  if (openedApp) {
    // Cashfree now genuinely targets GPay/PhonePe/Paytm by name (2026-09-13
    // — see the Cashfree tile grid below and cashfreeUtils.js's
    // CASHFREE_APP_PROVIDERS comment), so show the real app label for those
    // same as Razorpay already does. Only the generic 'upi_app' fallback tile
    // (no specific app targeted, Cashfree can't promise which app answers)
    // still shows the generic wording.
    const appLabel = (isCashfree && openedApp === 'upi_app')
      ? 'your UPI app'
      : (ALL_UPI_APPS.find(a => a.id === openedApp)?.label || openedApp);
    return (
      <div className={styles.awaitingPayment}>
        <div className={styles.spinner} />
        <p>Opened <strong>{appLabel}</strong>. Waiting for payment…</p>
        <p className={styles.awaitingHint}>Complete the payment in your UPI app. This page will update automatically.</p>
        <button className={styles.linkBtn} onClick={onChangeApp}>
          {isCashfree ? 'Try again' : 'Try a different app'}
        </button>
      </div>
    );
  }

  // ── Collect form (Add UPI ID) ──
  if (showCollect) {
    return (
      <div className={styles.upiIntentSection}>
        <button className={styles.upiBackLink} onClick={() => setShowCollect(false)}>
          ← Back to UPI apps
        </button>
        <UpiCollectSection
          amount={amount}
          processing={processing}
          onPay={(d) => onPay({ method: 'upi', ...d })}
        />
      </div>
    );
  }

  // ── Desktop: QR Code button + UPI ID form ──
  if (!isMobile) {
    return (
      <div className={styles.upiIntentSection}>
        {/* Prominent QR Code button */}
        <div className={styles.desktopQrRow}>
          <button
            className={styles.desktopQrBtn}
            onClick={() => onQrRequest && onQrRequest('upi')}
            disabled={processing}
          >
            <img src={qrCodeIcon} alt="QR Code" className={styles.desktopQrIcon} />
            <span className={styles.desktopQrLabel}>
              <span className={styles.desktopQrTitle}>Scan QR Code</span>
              <span className={styles.desktopQrHint}>Open GPay, PhonePe or any UPI app</span>
            </span>
            <span className={styles.desktopQrArrow}>›</span>
          </button>
        </div>

        {/* Divider */}
        <div className={styles.desktopQrDivider}>
          <span>or pay using UPI ID</span>
        </div>

        {/* UPI ID collect form */}
        <UpiCollectSection
          amount={amount}
          processing={processing}
          onPay={(d) => onPay({ method: 'upi', ...d })}
        />
      </div>
    );
  }

  return (
    <div className={styles.upiIntentSection}>
      {isCashfree ? (
        /* Real per-app targeting (2026-09-13) via payment_method.app —
           see cashfreeUtils.js's CASHFREE_APP_PROVIDERS comment. Same grid
           styling as Razorpay's app grid below, just a fixed 3-app list
           (Cashfree has no installed-app detection) plus one generic
           fallback tile for anything else. */
        <div className={styles.upiAppGrid}>
          {cashfreeAppsToShow.map((app) => (
            <button
              key={app.id}
              className={styles.upiAppBtn}
              onClick={() => onPay({ method: 'upi-intent', app: app.id })}
              disabled={processing}
              aria-label={`Pay with ${app.label}`}
            >
              <img
                src={app.icon}
                alt={app.label}
                className={styles.upiAppIcon}
                onError={(e) => {
                  if (e.target.src !== app.fallback) e.target.src = app.fallback;
                }}
              />
              <span className={styles.upiAppLabel}>{app.label}</span>
            </button>
          ))}
          <button
            className={styles.upiAppBtn}
            onClick={() => onPay({ method: 'upi-intent', app: 'upi_app' })}
            disabled={processing}
            aria-label="Pay with any other UPI app"
          >
            <img src={upiLogo} alt="Other UPI App" className={styles.upiAppIcon} />
            <span className={styles.upiAppLabel}>Other UPI App</span>
          </button>
        </div>
      ) : (
        /* 2-column app grid (mobile only) */
        <div className={styles.upiAppGrid}>
          {appsToShow.map((app) => (
            <button
              key={app.id}
              className={styles.upiAppBtn}
              onClick={() => onPay({ method: 'upi-intent', app: app.id })}
              disabled={processing}
              aria-label={`Pay with ${app.label}`}
            >
              <img
                src={app.icon}
                alt={app.label}
                className={styles.upiAppIcon}
                onError={(e) => {
                  if (e.target.src !== app.fallback) e.target.src = app.fallback;
                }}
              />
              <span className={styles.upiAppLabel}>{app.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Bottom action row */}
      <div className={styles.upiBottomRow}>
        <button
          className={styles.upiAddIdBtn}
          onClick={() => setShowCollect(true)}
          disabled={processing}
        >
          + Add UPI ID
        </button>
        <button
          className={styles.upiQrBtn}
          onClick={() => onQrRequest && onQrRequest('upi')}
          disabled={processing}
        >
          📷 QR Code
        </button>
      </div>
    </div>
  );
}

export default UpiIntentSection;
